import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import propTypes from "@styled-system/prop-types";
import { useController } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { pick, omit } from "lodash";
import {
  FormCheckboxWrapper,
  FormCheckboxLabelStyle,
  FormLabelStyle,
  FormCheckboxStyle,
  FormHelpStyle, FormInvalidFeedbackStyle,
} from "./forms.style";

const FormCheckbox = ({
  innerRef,
  validationRules,
  watch,
  control,
  formState,
  inline,
  label,
  name,
  defaultChecked,
  id,
  helpText,
  onChange,
  ...props
}) => {
  const [isRequired] = useState(!!validationRules?.required);
  const [error, setError] = useState(formState.errors ? formState.errors[name] : undefined);
  const inputId = id || `checkbox_${uuidv4()}`;
  const spacePropsKeys = Object.keys(propTypes.space);
  const spaceProps = pick(props, spacePropsKeys);
  const attributes = omit(props, spacePropsKeys);
  const {
    field: { onChange: oc, onBlur: ob, value, ref: originalCheckbox },
    meta: { invalid },
  } = useController({
    name,
    control,
    rules: validationRules,
    defaultValue: defaultChecked,
  });

  /* Set field error */
  useEffect(() => {
    if (!formState.errors) {
      setError(undefined);
    } else if (formState.errors[name] !== error) {
      setError(formState.errors[name]);
    }
  }, [formState, name, error]);

  const onCheckboxChange = (e) => {
    oc(e.target.checked);
    onChange && onChange(e);
  };

  return (
    <FormCheckboxWrapper {...spaceProps} inline={inline} ref={innerRef}>
      {!inline && <FormLabelStyle invalid={invalid} htmlFor={inputId}>{label}{isRequired && ` *`}</FormLabelStyle>}
      <FormCheckboxStyle
        ref={originalCheckbox}
        type="checkbox"
        name={name}
        id={inputId}
        checked={value}
        onChange={onCheckboxChange}
        onBlur={ob}
        inline={inline}
        {...attributes}
      />
      <FormCheckboxLabelStyle inline={inline} invalid={invalid} htmlFor={inputId}>
        {inline && <>{label}{isRequired && ` *`}</>}
      </FormCheckboxLabelStyle>
      {helpText && (
        <>
          <div css={{ flexBasis: "100%", height: 0, marginBottom: !inline ? 18.5 : null }} />
          <FormHelpStyle>{helpText}</FormHelpStyle>
        </>
      )}
      {error && (
        <>
          {!inline && <div css={{ flexBasis: "100%", height: 0, marginBottom: !helpText ? 18.5 : null }} />}
          <FormInvalidFeedbackStyle role="alert">{error.message}</FormInvalidFeedbackStyle>
        </>
      )}
    </FormCheckboxWrapper>
  );
};

FormCheckbox.propTypes = {
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  validationRules: PropTypes.object,
  watch: PropTypes.func,
  control: PropTypes.object,
  formState: PropTypes.object,
  inline: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  defaultChecked: PropTypes.bool,
  id: PropTypes.string,
  helpText: PropTypes.string,
  onChange: PropTypes.func,
  ...propTypes.space,
};

FormCheckbox.defaultProps = {
  inline: false,
  disabled: false,
  defaultChecked: false,
};

export default FormCheckbox;
